// file: pharmakon/client/src/components/admin/AdminNotifBell.jsx
/**
 * PHARMAKON — Admin Notification Bell
 *
 * Standalone component used in the admin AppShell topbar.
 * Polls GET /comms/admin-notifs/count every 20s.
 * Dropdown shows latest 10 notifications with type icons, level colors,
 * mark-read, archive, and "Voir tout" link.
 *
 * Config (all from CONFIG object below — no hardcoded values):
 *   POLL_INTERVAL_MS  — polling cadence
 *   DROPDOWN_LIMIT    — items shown in dropdown
 *   TYPE_META         — icon + label per AdminNotifType
 *   NIVEAU_STYLE      — color style per NiveauAlerte
 *
 * Keyboard (XWP-63): every row is a button (Tab, Entrée / Espace opens its `action_url` — a ticket
 * notification opens `/admin/tickets?ticket=<id>`); Échap closes the dropdown and returns the focus
 * to the bell.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import * as adminApi from "../../services/adminApi.js";

// ─── CONFIG ──────────────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 20_000;
const DROPDOWN_LIMIT = 10;

const TYPE_META = Object.freeze({
  COMPTE_DEMANDE: { icon: "🏥", label: "Demande compte" },
  PAIEMENT_RECU: { icon: "💳", label: "Paiement reçu" },
  TICKET_OUVERT: { icon: "🎫", label: "Nouveau ticket" },
  TICKET_REPONSE: { icon: "💬", label: "Réponse ticket" },
  SERVICE_ALERTE: { icon: "🔴", label: "Alerte service" },
  LICENCE_EXPIREE: { icon: "⏰", label: "Licence expirée" },
  CNAS_IMPORT: { icon: "📋", label: "Import CNAS" },
  FLAG_ADMIN: { icon: "🚩", label: "Signalement" },
  NOTE_ADMIN: { icon: "📝", label: "Note admin" },
  BROADCAST_ENVOYE: { icon: "📢", label: "Broadcast envoyé" },
  AUTRE: { icon: "ℹ️", label: "Événement" },
});

// Color per niveau for the left accent border + dot (var(--token) based)
const NIVEAU_STYLE = (n) =>
  ({
    INFO: { border: "var(--accent)", dot: "var(--accent)", soft: "var(--accent-soft)" },
    WARNING: { border: "var(--warning)", dot: "var(--warning)", soft: "var(--warning-soft)" },
    CRITICAL: { border: "var(--danger)", dot: "var(--danger)", soft: "var(--danger-soft)" },
  })[n] ?? { border: "var(--accent)", dot: "var(--accent)", soft: "var(--accent-soft)" };

// Relative time helper
function ago(ts) {
  const s = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}min`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}j`;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function AdminNotifBell() {
  const navigate = useNavigate();

  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);
  const dropRef = useRef(null);
  const bellRef = useRef(null);

  // ── Poll unread count ────────────────────────────────────────────────────────
  const fetchCount = useCallback(async () => {
    try {
      const r = await adminApi.getAdminNotifCount();
      setCount(r.data?.count ?? 0);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchCount();
    pollRef.current = setInterval(fetchCount, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchCount]);

  // ── Load dropdown on open ────────────────────────────────────────────────────
  const openDropdown = useCallback(async () => {
    setOpen(true);
    setLoading(true);
    try {
      const r = await adminApi.listAdminNotifs({
        limit: DROPDOWN_LIMIT,
        archivee: false,
      });
      setItems(r.data ?? []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  // Click-outside close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // ── Actions ──────────────────────────────────────────────────────────────────
  const markRead = async (id, e) => {
    e.stopPropagation();
    await adminApi.markAdminNotifRead(id).catch(() => {});
    setItems((prev) =>
      prev.map((n) => (n.id === id ? { ...n, lue: true } : n)),
    );
    setCount((prev) => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await adminApi.markAllAdminNotifsRead().catch(() => {});
    setItems((prev) => prev.map((n) => ({ ...n, lue: true })));
    setCount(0);
  };

  const handleItemClick = async (item) => {
    if (!item.lue) {
      await adminApi.markAdminNotifRead(item.id).catch(() => {});
      setCount((prev) => Math.max(0, prev - 1));
    }
    setOpen(false);
    if (item.action_url) navigate(item.action_url);
  };

  const badgeText = count > 99 ? "99+" : count > 0 ? String(count) : null;

  const onDropKeyDown = (e) => {
    if (e.key !== "Escape") return;
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    bellRef.current?.focus();
  };
  const onRowKeyDown = (item) => (e) => {
    if (e.target !== e.currentTarget) return; // the « ✓ » button keeps its own keys
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleItemClick(item);
    }
  };

  return (
    <div style={{ position: "relative" }} ref={dropRef} onKeyDown={open ? onDropKeyDown : undefined}>
      {/* Bell button */}
      <button
        ref={bellRef}
        type="button"
        aria-label={count > 0 ? `Notifications admin, ${count} non lue${count > 1 ? "s" : ""}` : "Notifications admin"}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={open ? () => setOpen(false) : openDropdown}
        style={{
          position: "relative",
          background: open ? "var(--accent-dim)" : "transparent",
          border: `1px solid ${open ? "var(--border-default)" : "var(--border-subtle)"}`,
          borderRadius: "var(--radius)",
          padding: "6px 8px",
          cursor: "pointer",
          color: "var(--text-1)",
          display: "flex",
          alignItems: "center",
          transition: "all 120ms",
        }}
        title="Notifications admin"
      >
        <svg
          viewBox="0 0 24 24"
          width={17}
          height={17}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
        </svg>

        {/* Badge */}
        {badgeText && (
          <span
            style={{
              position: "absolute",
              top: -6,
              right: -6,
              background: "var(--danger)",
              color: "var(--accent-fg)",
              borderRadius: "var(--radius-sm)",
              fontSize: 9,
              fontWeight: 900,
              padding: "1px 4px",
              minWidth: 16,
              textAlign: "center",
              lineHeight: "14px",
              border: `2px solid ${"var(--bg)"}`,
              animation: "pulse 2s infinite",
            }}
          >
            {badgeText}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 380,
            zIndex: 300,
            background: "var(--surf)",
            border: `1px solid ${"var(--border-default)"}`,
            borderRadius: "var(--radius-lg)",
            boxShadow: "var(--shadow-panel)",
            overflow: "hidden",
            animation: "notifSlideIn 140ms ease",
          }}
        >
          <style>{`
            @keyframes notifSlideIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
            @keyframes pulse{0%,100%{opacity:1}50%{opacity:.6}}
          `}</style>

          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 14px",
              borderBottom: `1px solid ${"var(--border-subtle)"}`,
            }}
          >
            <span style={{ fontSize: 12.5, fontWeight: 800, color: "var(--text-0)" }}>
              Notifications Admin
              {count > 0 && (
                <span
                  style={{
                    marginLeft: 7,
                    background: "var(--danger-soft)",
                    color: "var(--danger)",
                    borderRadius: "var(--radius-sm)",
                    fontSize: 10,
                    fontWeight: 900,
                    padding: "1px 6px",
                  }}
                >
                  {count} non lues
                </span>
              )}
            </span>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {count > 0 && (
                <button
                  onClick={markAllRead}
                  style={{
                    fontSize: 10,
                    color: "var(--accent)",
                    fontWeight: 700,
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: "2px 6px",
                    borderRadius: "var(--radius-sm)",
                  }}
                >
                  Tout lire
                </button>
              )}
              <button
                onClick={() => {
                  setOpen(false);
                  navigate("/admin/notifications");
                }}
                style={{
                  fontSize: 10,
                  color: "var(--text-2)",
                  fontWeight: 600,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "2px 6px",
                  borderRadius: "var(--radius-sm)",
                }}
              >
                Voir tout →
              </button>
            </div>
          </div>

          {/* List */}
          <div style={{ maxHeight: 420, overflowY: "auto" }}>
            {loading && (
              <div
                style={{
                  padding: 24,
                  textAlign: "center",
                  color: "var(--text-3)",
                  fontSize: 11,
                }}
              >
                Chargement…
              </div>
            )}
            {!loading && items.length === 0 && (
              <div
                style={{
                  padding: 32,
                  textAlign: "center",
                  color: "var(--text-3)",
                  fontSize: 12,
                }}
              >
                Aucune notification
              </div>
            )}
            {items.map((item) => {
              const tm = TYPE_META[item.type] ?? TYPE_META.AUTRE;
              const ns = NIVEAU_STYLE(item.niveau);
              return (
                <div
                  key={item.id}
                  role="button"
                  tabIndex={0}
                  data-admin-notif-row=""
                  onClick={() => handleItemClick(item)}
                  onKeyDown={onRowKeyDown(item)}
                  style={{
                    display: "flex",
                    gap: 10,
                    padding: "10px 14px",
                    cursor: "pointer",
                    background: item.lue ? "transparent" : "var(--accent-dim)",
                    borderLeft: `3px solid ${item.lue ? "transparent" : ns.border}`,
                    borderBottom: `1px solid ${"var(--border-subtle)"}`,
                    transition: "background 150ms",
                  }}
                >
                  {/* Icon */}
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "var(--radius)",
                      background: "var(--raised)",
                      border: `1px solid ${"var(--border-subtle)"}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 15,
                      flexShrink: 0,
                    }}
                  >
                    {tm.icon}
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 6,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11.5,
                          fontWeight: item.lue ? 500 : 800,
                          color: "var(--text-0)",
                          lineHeight: 1.3,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 200,
                        }}
                      >
                        {item.titre}
                      </span>
                      <span
                        style={{ fontSize: 9.5, color: "var(--text-3)", flexShrink: 0 }}
                      >
                        {ago(item.created_at)}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: 10.5,
                        color: "var(--text-2)",
                        marginTop: 2,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.message}
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginTop: 4,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: ns.dot,
                          background: ns.soft,
                          padding: "1px 5px",
                          borderRadius: "var(--radius-sm)",
                        }}
                      >
                        {tm.label}
                      </span>
                      {item.pharmacie && (
                        <span style={{ fontSize: 9, color: "var(--text-3)" }}>
                          {item.pharmacie.nom}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Unread dot + mark read */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 6,
                      flexShrink: 0,
                    }}
                  >
                    {!item.lue && (
                      <>
                        <div
                          style={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            background: ns.dot,
                          }}
                        />
                        <button
                          onClick={(e) => markRead(item.id, e)}
                          title="Marquer comme lu"
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "var(--text-3)",
                            fontSize: 10,
                            padding: 2,
                          }}
                        >
                          ✓
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "9px 14px",
              borderTop: `1px solid ${"var(--border-subtle)"}`,
              textAlign: "center",
            }}
          >
            <button
              onClick={() => {
                setOpen(false);
                navigate("/admin/notifications");
              }}
              style={{
                fontSize: 11,
                color: "var(--accent)",
                fontWeight: 700,
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              Voir toutes les notifications →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
